import { ArrowLeft, Flag, ShieldOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ChipSelect, Field, HexAvatar, LevelMark, Loading, ProgressBar } from '../components';
import { formatRelative } from '../format';
import { Link } from '../router';
import { useApp } from '../store';
import { PLAY_STYLE_LABELS, VOICE_LABELS, AVAILABILITY_OPTIONS, type PlayerProfile } from '../types';

interface Commend {
  fromUsername: string;
  tags: string[];
  createdAt: string;
}

const REPORT_REASONS = [
  { value: 'harassment', label: 'Harassment' },
  { value: 'cheating', label: 'Cheating' },
  { value: 'no-show', label: 'No-show' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Other' }
];

function SafetyActions({ player }: { player: PlayerProfile }) {
  const { authStatus, user, toast } = useApp();
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [blocked, setBlocked] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authStatus !== 'authed') return;
    api.blocks()
      .then((data) => setBlocked(data.blocks.some((block) => block.userId === player.id)))
      .catch(() => setBlocked(false));
  }, [authStatus, player.id]);

  if (authStatus !== 'authed' || user?.id === player.id) return null;

  async function toggleBlock() {
    setBusy(true);
    try {
      if (blocked) {
        await api.unblockPlayer(player.id);
        setBlocked(false);
        toast(`${player.displayName} unblocked.`);
      } else {
        if (!window.confirm(`Block ${player.displayName}? You won't see each other's sessions, and they can't join yours.`)) return;
        await api.blockPlayer(player.id);
        setBlocked(true);
        toast(`${player.displayName} blocked.`, 'success');
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : 'That did not work.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function submitReport(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reason) {
      toast('Pick a reason for the report.', 'error');
      return;
    }
    setBusy(true);
    try {
      const result = await api.reportPlayer(player.id, reason, details);
      toast(result.message, 'success');
      setReporting(false);
      setReason('');
      setDetails('');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not submit the report.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="safety-actions">
      <div className="action-bar">
        <button className="ghost small" disabled={busy || blocked === null} onClick={toggleBlock}>
          <ShieldOff size={14} /> {blocked ? 'Unblock' : 'Block'}
        </button>
        <button className="ghost small" onClick={() => setReporting((current) => !current)}>
          <Flag size={14} /> {reporting ? 'Close report' : 'Report'}
        </button>
      </div>
      {reporting && (
        <form className="stack-form edit-form" onSubmit={submitReport}>
          <Field label="Reason">
            <ChipSelect options={REPORT_REASONS} selected={reason ? [reason] : []} onToggle={(value) => setReason(value === reason ? '' : value)} single />
          </Field>
          <Field label="What happened?" hint="Reports go to the TeamCraft team, never to the player.">
            <textarea value={details} onChange={(event) => setDetails(event.target.value)} maxLength={1000} rows={3} />
          </Field>
          <button className="primary small" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Submit report'}</button>
        </form>
      )}
    </div>
  );
}

export function PlayerDetail({ id }: { id: string }) {
  const { games } = useApp();
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [commends, setCommends] = useState<Commend[]>([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.player(id)
      .then((data) => {
        setPlayer(data.player);
        setCommends(data.recentCommends);
      })
      .catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="page narrow">
        <h1>Player not found</h1>
        <Link to="/players" className="secondary small">Back to players</Link>
      </div>
    );
  }
  if (!player) return <Loading label="Loading player…" />;

  const gameNames = player.games
    .map((slug) => games.find((game) => game.slug === slug)?.name ?? slug);
  const availabilityLabels = player.availability
    .map((slug) => AVAILABILITY_OPTIONS.find((option) => option.slug === slug)?.label ?? slug);

  return (
    <div className="page narrow">
      <Link to="/players" className="back-link"><ArrowLeft size={15} /> All players</Link>

      <div className="profile-head">
        <HexAvatar label={player.avatar} size="lg" icon={player.avatarIcon} accent={player.avatarColor} />
        <div>
          <h1>{player.displayName}</h1>
          <p className="muted">@{player.username}{player.pronouns ? ` · ${player.pronouns}` : ''}{player.timezone ? ` · ${player.timezone}` : ''}</p>
          <LevelMark level={player.progression.level} title={player.progression.title} />
        </div>
      </div>

      {player.bio && <p className="detail-description">{player.bio}</p>}

      <SafetyActions player={player} />

      <section className="dash-stats" aria-label="Reputation">
        <div><strong>{player.reputation.sessionsCompleted}</strong><span>Sessions played</span></div>
        <div><strong>{player.reputation.playAgainRate === null ? '—' : `${player.reputation.playAgainRate}%`}</strong><span>Would play again</span></div>
        <div><strong>{player.reputation.confirmedTeammates}</strong><span>New teammates</span></div>
        <div><strong className="mono">{player.progression.points}</strong><span>Points</span></div>
      </section>
      <ProgressBar value={player.progression.progress} />

      <section className="profile-facts">
        {gameNames.length > 0 && <div><span className="field-label">Games</span><div className="tag-row">{gameNames.map((name) => <span key={name}>{name}</span>)}</div></div>}
        {player.platforms.length > 0 && <div><span className="field-label">Platforms</span><div className="tag-row">{player.platforms.map((item) => <span key={item}>{item}</span>)}</div></div>}
        <div><span className="field-label">Play style</span><div className="tag-row"><span>{PLAY_STYLE_LABELS[player.playStyle]}</span><span>{VOICE_LABELS[player.voice]}</span></div></div>
        {availabilityLabels.length > 0 && <div><span className="field-label">Usually plays</span><div className="tag-row">{availabilityLabels.map((label) => <span key={label}>{label}</span>)}</div></div>}
        {player.languages.length > 0 && <div><span className="field-label">Languages</span><div className="tag-row">{player.languages.map((item) => <span key={item}>{item}</span>)}</div></div>}
      </section>

      {commends.length > 0 && (
        <section aria-label="Recent commends">
          <div className="section-heading slim"><h2>Recent commends</h2></div>
          {commends.map((commend, index) => (
            <div key={index} className="commend-row">
              <span>{commend.fromUsername} would play again</span>
              <span className="roster-tags">{commend.tags.map((tag) => <span key={tag} className="pill mint">{tag}</span>)}</span>
              <span className="mono muted">{formatRelative(commend.createdAt)}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
